// @givmo/org-console — the host's configuration, and the ONE seam the entity class enters through.
//
// ══ THE SEAM IS DATA, NOT A DISCRIMINANT ═══════════════════════════════════════════════════════
//
// This console is built ONCE, keyed on `orgRef`, and it never learns what KIND of organization it is
// rendering. The words a host calls things arrive as {@link OrgConsoleVocab} — strings and label
// functions the package renders verbatim — and there is deliberately no class field to branch on.
//
// That is not tidiness. The backend resolves the class from `(user, org_ref)` precisely so the wire
// never carries one: a client that could name the class its own organization is judged under is a
// client that can pick its own authorization rules. A `class` field here would put that fork back
// into the shared machinery, where it is hardest to remove — and every branch on it would be an
// authorization decision made on the wrong side of the wire.
//
// Pinned by `org-generic.test.ts`, which is the only mechanical guard the web half of that rule has.

/**
 * The words one host calls things. Rendered verbatim; never branched on.
 *
 * The label functions exist because module slugs and permission keys are the BACKEND's vocabulary
 * and a host may know copy for some of them. Both have a titleizing default (see
 * {@link titleizeSlug}) so a slug this host has no copy for still renders as something a person can
 * act on — a control with a blank label beside a live control is unusable.
 */
export interface OrgConsoleVocab {
  /** What this host calls the organization, in running copy. */
  orgNoun: string;
  /**
   * The organization's OWN name, when the host has resolved it.
   *
   * ⚠ THE SWITCH PAGE IS THE ONE SURFACE THAT MUST NAME IT. Turning a module off closes it for
   * everyone in the organization, and at phone width nothing else on that page says WHICH: the bar
   * shows the leaf, the heading repeats it, and the org name lives only inside a closed drawer. Every
   * sibling surface carries it in content — the grid as its title, the roster in its description, a
   * module page in its subtitle — and this is the page where a wrong-org flip is most expensive.
   *
   * Optional because a host may not have it yet; the copy falls back to `orgNoun` rather than
   * printing a blank where a name belongs.
   */
  orgName?: string;
  /** The roster panel's heading. */
  membersTitle: string;
  /** The roster panel's one-line description, already naming the organization if the host wants to. */
  membersDescription: string;
  /** This host's word for a module slug. */
  moduleLabel: (slug: string) => string;
  /** This host's sentence for a catalog permission key. */
  permissionLabel: (key: string) => string;
  /**
   * The org-account deactivation act's copy for this class (Org Machinery OM-9) — see
   * {@link OrgDeactivationCopy}. Absent ⇒ the *Deactivate organization* control never renders,
   * whatever the standing read says: a control whose consequences this host has not written is a
   * control that would lie by omission.
   */
  deactivation?: OrgDeactivationCopy;
  /**
   * A line this host adds to the *Leave organization* card, after the package's own (D-CPO-69
   * question 15: what leaving does NOT touch, in this class's words). Absent ⇒ the card says the
   * package's line alone.
   */
  leaveNote?: string;
}

/**
 * A consequence that reads one way where deactivating clears an address the organization entered,
 * shown on the page or not (D-CPO-62: the act clears the physical address; D-CPO-69 question 8's
 * condition, widened by OM-92), and another way everywhere else.
 */
export interface OrgDeactivationConditionalLine {
  /** The line where no address was entered: the act deletes nothing. */
  line: string;
  /** The line where an address was entered, shown or hidden: the act clears it. */
  whereEnteredAddress: string;
}

/** One consequence: a plain sentence, or one that depends on an entered address. */
export type OrgDeactivationConsequence = string | OrgDeactivationConditionalLine;

/**
 * What org-account deactivation DOES to an organization of this class, as data.
 *
 * The act is one org-generic operation, but its consequences differ per entity class — which
 * surfaces freeze, what stays listed, what money does, how the organization comes back — and this
 * package never learns the class. So the host writes the sentences and this package renders them
 * verbatim, in the order given, above the typed confirmation. Every line is a plain statement of
 * what happens; the re-enrollment truth for the class is the host's last line.
 */
export interface OrgDeactivationCopy {
  /** The class's noun in running copy ("Freezes the <noun>'s Givmo account…"). */
  noun: string;
  /** The consequences, in reading order — one plain sentence each, the re-enrollment line last. */
  consequences: readonly OrgDeactivationConsequence[];
}

/**
 * Everything one mount of the console needs.
 *
 * `orgRef` is the ONLY scope input — every read and every write is addressed under it, and the
 * package holds no other notion of "which organization am I looking at".
 */
export interface OrgConsoleConfig {
  /** The organization this console is scoped to. The ONLY scope input. */
  orgRef: string;
  /** Words this host calls things — see {@link OrgConsoleVocab}. */
  vocab: OrgConsoleVocab;
  /**
   * The signed-in admin's own BACKEND user id, when this host knows it.
   *
   * ⚠ THE PACKAGE CANNOT DERIVE THIS AND MUST NOT GUESS IT. `GET /org/{org_ref}/me` reports the
   * caller's STANDING, not their identity, and matching a standing back onto a roster row is a
   * guess that puts "this is you" on somebody else's row — worse than saying nothing. Every roster
   * row is keyed by the backend-internal user id, which is a different id-space from a Firebase uid,
   * so a host that has only the latter passes `null` and the editor simply omits its
   * self-demotion warning.
   */
  selfUserId?: string | null;
  /**
   * The slug of this class's DEFAULT LANDING module (shell contract v3, T1.4), when the host's
   * table names one.
   *
   * ⚠ THE APPS PAGE LEADS WITH ITS CARD (D-CP-54, Jonathan 2026-09-04). The management grid
   * (`OrgModulesPanel`) renders the class default's card FIRST and keeps every other card in the
   * settings read's own order — the wire's order, which appends a class's newest module LAST.
   * Absent ⇒ the wire order exactly as before, byte for byte. A slug the settings map does not
   * carry ⇒ no effect: never a phantom card, never an error — which modules exist still comes from
   * the settings read alone.
   */
  classDefault?: string;
  /**
   * The apps a person can OPEN in this console here — the grid's set (Org Machinery OM-95).
   *
   * ⚠ EVERY LIST THAT OFFERS APPS FOLLOWS IT: the invite on both doors, the access editor, the Apps
   * page's switches, the roster's app count and an invite's app summary. The catalog the backend
   * serves is the class's apps, released and offered; it cannot know which of them this build
   * mounts, so without this a list offers an app nobody here can open (Compliance and Board on prod,
   * where their pages are dark). The host derives it from the SAME table its squares derive from
   * ({@link servedModuleSlugs}), so a list and the grid can never disagree.
   *
   * Absent ⇒ no narrowing, byte for byte as before. Hiding an app never revokes it: a list that
   * leaves it out sends nothing about it.
   */
  servedModules?: readonly string[];
  /**
   * Where this host's own routes live, so the package emits no route literals of its own.
   *
   * ⚠ EVERY ENTRY IS OPTIONAL AND ITS ABSENCE MEANS NO CONTROL, never a guessed path. `moduleAccess`
   * is the per-module door out of the management grid — "who can open this one" — which is the roster
   * filtered to that module. A host that has not routed it renders no link rather than one that goes
   * nowhere.
   */
  links?: {
    member?: (userId: string) => string;
    moduleAccess?: (module: string) => string;
    /**
     * The organization's own Apps page (the switches) — where "Turn on <app> first" and a
     * setting-up tile's line go. Absent ⇒ no link, a statement instead.
     */
    apps?: string;
    /** The Team access page — where the Apps page's pending-requests strip goes. Absent ⇒ no link. */
    teamAccess?: string;
  };
  /**
   * Called after a write on this surface has landed and this package's own caches have been
   * invalidated.
   *
   * ⚠ FOR A HOST THAT RENDERS SOMETHING DERIVED FROM THIS AUTHORITY, AND FOR NOTHING ELSE. The
   * package invalidates every key it owns, and it cannot know that a host also draws a surface off
   * the same facts through a read of its own — an app grid filtered by the caller's grants, a
   * navigation row that appears only while a module is switched on. Those reads are keyed by the
   * host, are invisible from here, and go stale the instant an access write lands.
   *
   * It is deliberately a bare notification rather than a list of keys: WHAT moved is this package's
   * business and what else depends on it is the host's, so handing over keys would both leak this
   * package's cache shape and invite a host to invalidate entries it does not own. A host with no
   * derived read passes nothing and behaves exactly as before.
   */
  onAccessWritten?: () => void;
  /**
   * Called after a write to the admin's OWN preferences has landed and this package's caches are
   * updated, with the preference keys it wrote.
   *
   * ⚠ THE SAME RULE AS {@link OrgConsoleConfig.onAccessWritten}: for a host that draws something off
   * one of those choices through a read of its own — the Compliance pages' notice for when nobody
   * gets deadline reminders reads the effect of `compliance.reminders.email` off the module's
   * profile — and for nothing else. The keys are wire data, not this package's cache shape; what
   * depends on them is the host's business. A host with no such read passes nothing.
   */
  onPreferencesWritten?: (keys: readonly string[]) => void;
  /**
   * Follow a `links` destination WITHOUT a document load, when this host can.
   *
   * ⚠ THE LINK IS STILL A REAL LINK. Every destination this package renders carries its `href` from
   * {@link OrgConsoleConfig.links}, so copy-link and open-in-new-tab keep working and a host that
   * supplies nothing here still navigates — it just pays a full page load. A host running a client
   * router passes its own navigation and gets the ordinary in-app hop. The package cannot do this
   * itself: it has no router, deliberately, because two hosts run different ones.
   */
  onOpenModuleAccess?: (module: string) => void;
  /**
   * Follow ANY `links` destination this package renders without a document load — the general
   * form of {@link OrgConsoleConfig.onOpenModuleAccess}, for the links shell contract v3 added
   * (`links.apps`, `links.teamAccess`). Same rule: the link is still a real link.
   */
  onNavigate?: (href: string) => void;
  /** Test-id namespace, so two mounts on one page cannot collide. Defaults to `org-console`. */
  testIdPrefix?: string;
}

/** The default test-id namespace. Part of the test-id contract — treat a change like an API change. */
export const DEFAULT_TEST_ID_PREFIX = 'org-console';

/** This mount's test-id namespace. */
export function testIdPrefix(config: OrgConsoleConfig): string {
  const prefix = (config.testIdPrefix ?? '').trim();
  return prefix.length > 0 ? prefix : DEFAULT_TEST_ID_PREFIX;
}

/**
 * Whether an app may appear in this mount's lists (OM-95) — always, when the host names no set.
 */
export function isServedModule(config: OrgConsoleConfig, module: string): boolean {
  const served = config.servedModules;
  return served === undefined || served.includes(module.trim());
}

/**
 * A readable word for a slug a host has no copy for — `general_ledger` → "General ledger".
 *
 * An empty slug renders as an em dash (there is nothing to name); anything else is titleized from
 * itself, so a module the backend launches before a host ships copy for it reads as a module rather
 * than as an identifier, and never as an empty cell.
 */
export function titleizeSlug(slug: string | null | undefined): string {
  const value = (slug ?? '').trim();
  if (value.length === 0) return '—';
  const words = value.replace(/[_-]+/g, ' ').trim();
  if (words.length === 0) return value;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A vocabulary that names nothing beyond what the wire already says.
 *
 * Hosts are expected to pass their own; this exists so a test, a story or an early mount is not
 * forced to invent copy, and so the two label defaults live in exactly one place.
 */
export function defaultOrgConsoleVocab(): OrgConsoleVocab {
  return {
    orgNoun: 'organization',
    membersTitle: 'Members',
    membersDescription: 'Who can act for this organization on Givmo.',
    moduleLabel: titleizeSlug,
    // A key the host has no sentence for renders as ITSELF, never as nothing: the catalog is served
    // by the backend and can grow a key before any host ships copy for it.
    permissionLabel: (key: string) => key,
  };
}
